import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, ShieldAlert, Siren, Wallet } from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { employerCockpitApi, ACTIEPUNTEN_KEY, type Actiepunt, type ActiepuntSoort } from '../services/employerCockpitApi';
import { tariefContractjaarApi, TARIEF_VOORSTELLEN_KEY } from '../services/tariefContractjaarApi';
import { TariefVoorstelActiepunten } from '../components/tariefContractjaar/TariefVoorstelActiepunten';

const ICOON: Record<ActiepuntSoort, React.ComponentType<{ className?: string }>> = {
  BUDGET_VERLENGING: Siren,
  GOVERNANCE_STANDBY: ShieldAlert,
  WALLET_RESTOCK: Wallet,
};

const ERNST_STIJL = {
  rood: { rand: 'border-l-red-500', badge: 'bg-red-100 text-red-800', icoon: 'text-red-600' },
  oranje: { rand: 'border-l-amber-400', badge: 'bg-amber-100 text-amber-800', icoon: 'text-amber-600' },
} as const;

const KNOP_BASIS = 'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const KNOP = {
  primair: `${KNOP_BASIS} bg-navy-900 text-white hover:bg-navy-800`,
  akkoord: `${KNOP_BASIS} bg-green-700 text-white hover:bg-green-800`,
  gevaar: `${KNOP_BASIS} bg-red-700 text-white hover:bg-red-800`,
  secundair: `${KNOP_BASIS} border border-gray-300 bg-white text-navy-900 font-medium hover:border-gray-400`,
};

const formatEuro = (n: number | string | null | undefined) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(Number(n ?? 0));

/**
 * Dashboard › Actiepunten: de chronologische prioriteitenlijst waarop de
 * opdrachtgever zelf 'go' moet geven. Drie triggers: budgetverlenging (Deel 5B),
 * OR-mandaat (art. 27 WOR) en een lege arbo-pot.
 */
export const EmployerActiepuntenPage: React.FC = () => {
  const { t, i18n } = useTranslation('employerDashboard');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useCockpitTitle(t('actiepunten.titel'), t('actiepunten.ondertitel'));

  const { data, isLoading, isError } = useQuery({
    queryKey: ACTIEPUNTEN_KEY,
    queryFn: employerCockpitApi.actiepunten,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Tariefvoorstellen voor het nieuwe contractjaar (indexatieslot, migratie 196); zelfde cache als TariefVoorstelActiepunten.
  const tariefVoorstellen = useQuery({
    queryKey: TARIEF_VOORSTELLEN_KEY,
    queryFn: tariefContractjaarApi.opdrachtgever,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const aantalTariefVoorstellen = tariefVoorstellen.data?.voorstellen.length ?? 0;

  const [afwijzenVoor, setAfwijzenVoor] = useState<string | null>(null);
  const [orOpen, setOrOpen] = useState(false);
  const [orVerklaard, setOrVerklaard] = useState(false);

  const besluit = useMutation({
    mutationFn: (v: { id: string; akkoord: boolean; extra: number }) => employerCockpitApi.besluitVerlenging(v.id, v.akkoord),
    onSuccess: (_d, v) => {
      toast.success(v.akkoord ? t('actiepunten.BUDGET_VERLENGING.akkoordGelukt', { extra: v.extra }) : t('actiepunten.BUDGET_VERLENGING.afgewezen'));
      setAfwijzenVoor(null);
      queryClient.invalidateQueries({ queryKey: ACTIEPUNTEN_KEY });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('actiepunten.fout')),
  });

  const orMandaat = useMutation({
    mutationFn: employerCockpitApi.accordeerOrMandaat,
    onSuccess: () => {
      toast.success(t('actiepunten.GOVERNANCE_STANDBY.gelukt'));
      setOrOpen(false);
      queryClient.invalidateQueries({ queryKey: ACTIEPUNTEN_KEY });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('actiepunten.fout')),
  });

  const datumF = useMemo(
    () => new Intl.DateTimeFormat(i18n.language?.startsWith('en') ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }),
    [i18n.language]
  );

  const tekst = (a: Actiepunt) => {
    const p = a.params;
    if (a.soort === 'WALLET_RESTOCK') {
      return t('actiepunten.WALLET_RESTOCK.tekst', { saldo: formatEuro(p.saldo), drempel: formatEuro(p.drempel), trajecten: p.trajecten });
    }
    return t(`actiepunten.${a.soort}.tekst`, p as Record<string, unknown>);
  };

  const acties = (a: Actiepunt) => {
    if (a.soort === 'WALLET_RESTOCK') {
      return (
        <button type="button" onClick={() => navigate('/employer/arbo-pot')} className={KNOP.primair}>
          {t('actiepunten.WALLET_RESTOCK.knop')}
        </button>
      );
    }
    if (!a.kan_handelen) {
      return (
        <p className="text-xs text-navy-500">
          {a.soort === 'GOVERNANCE_STANDBY' ? t('actiepunten.GOVERNANCE_STANDBY.alleenHoofdgebruiker') : t('actiepunten.alleenLezen')}
        </p>
      );
    }
    if (a.soort === 'GOVERNANCE_STANDBY') {
      return orOpen ? (
        <div className="w-full rounded-lg border border-red-200 bg-red-50/60 p-3 space-y-3">
          <label className="flex items-start gap-2 text-sm text-navy-800">
            <input type="checkbox" className="mt-1 rounded" checked={orVerklaard} onChange={(e) => setOrVerklaard(e.target.checked)} />
            <span>{t('actiepunten.GOVERNANCE_STANDBY.verklaring')}</span>
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!orVerklaard || orMandaat.isPending}
              onClick={() => orMandaat.mutate()}
              className={KNOP.primair}
            >
              {t('actiepunten.GOVERNANCE_STANDBY.bevestig')}
            </button>
            <button type="button" onClick={() => setOrOpen(false)} className={KNOP.secundair}>
              {t('actiepunten.BUDGET_VERLENGING.annuleren')}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setOrOpen(true)} className={KNOP.primair}>
          {t('actiepunten.GOVERNANCE_STANDBY.knop')}
        </button>
      );
    }
    // BUDGET_VERLENGING
    const id = String(a.params.verzoek_id);
    const extra = Number(a.params.extra);
    const bezig = besluit.isPending && besluit.variables?.id === id;
    if (afwijzenVoor === id) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-navy-600">{t('actiepunten.BUDGET_VERLENGING.afwijzenZeker')}</span>
          <button type="button" disabled={bezig} onClick={() => besluit.mutate({ id, akkoord: false, extra })} className={KNOP.gevaar}>
            {t('actiepunten.BUDGET_VERLENGING.jaAfwijzen')}
          </button>
          <button type="button" onClick={() => setAfwijzenVoor(null)} className={KNOP.secundair}>
            {t('actiepunten.BUDGET_VERLENGING.annuleren')}
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={bezig} onClick={() => besluit.mutate({ id, akkoord: true, extra })} className={KNOP.akkoord}>
          <CheckCircle2 className="w-4 h-4" />
          {t('actiepunten.BUDGET_VERLENGING.akkoord')}
        </button>
        <button type="button" disabled={bezig} onClick={() => setAfwijzenVoor(id)} className={KNOP.secundair}>
          {t('actiepunten.BUDGET_VERLENGING.afwijzen')}
        </button>
      </div>
    );
  };

  const items = data?.items ?? [];

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex justify-end">
        <Link to="/employer/arbo-pot" className="inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:text-navy-900">
          <Wallet className="w-4 h-4" />
          {t('actiepunten.arboPot')}
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-gray-200 animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {t('actiepunten.laadFout')}
        </div>
      ) : items.length === 0 && aantalTariefVoorstellen === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-navy-900">{t('actiepunten.leegTitel')}</p>
          <p className="text-sm text-navy-500 mt-1">{t('actiepunten.leegTekst')}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          <TariefVoorstelActiepunten />
          {items.map((a) => {
            const stijl = ERNST_STIJL[a.ernst];
            const Icon = ICOON[a.soort];
            return (
              <li key={a.id} className={`rounded-xl border border-gray-200 border-l-4 ${stijl.rand} bg-white shadow-sm p-4`}>
                <div className="flex items-start gap-3">
                  <Icon className={`w-6 h-6 flex-shrink-0 mt-0.5 ${stijl.icoon}`} />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${stijl.badge}`}>
                        {t(`actiepunten.ernst.${a.ernst}`)}
                      </span>
                      <h2 className="text-sm font-semibold text-navy-900">{t(`actiepunten.${a.soort}.label`)}</h2>
                      <span className="text-xs text-navy-400">{t('actiepunten.sinds', { datum: datumF.format(new Date(a.sinds)) })}</span>
                    </div>
                    <p className="text-sm text-navy-700">{tekst(a)}</p>
                    <div className="pt-1">{acties(a)}</div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
